import React, { useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const HERO_IMG = "https://images.pexels.com/photos/23897798/pexels-photo-23897798.jpeg?auto=compress&cs=tinysrgb&w=1600";

export default function Login() {
  const { user, login, error } = useAuth();
  const [email, setEmail] = useState("coach@titans.football");
  const [password, setPassword] = useState("titans2026");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  if (user && user !== false && user !== null) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await login(email, password);
    setBusy(false);
    if (ok) nav("/", { replace: true });
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-background">
      {/* Left — hero */}
      <div className="relative hidden lg:block overflow-hidden">
        <img src={HERO_IMG} alt="American football players in formation" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0D12] via-[#0B0D12]/70 to-[#0B0D12]/40" />
        <div className="absolute inset-0 flex flex-col justify-between p-12">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber flex items-center justify-center">
                <span className="font-display text-3xl text-[#0B0D12] leading-none">G</span>
              </div>
              <div>
                <div className="font-display text-3xl tracking-widest text-white">GRIDIRON OS</div>
                <div className="font-condensed text-[10px] tracking-[0.4em] uppercase text-amber">The Program Operating System</div>
              </div>
            </div>
          </div>
          <div className="max-w-xl">
            <div className="hairline mb-6" />
            <div className="font-display text-6xl leading-[0.95] tracking-wide text-white uppercase">
              Every play.<br/>Every practice.<br/>
              <span className="text-amber">Every philosophy.</span>
            </div>
            <p className="text-slate-300 mt-6 leading-relaxed font-light max-w-md">
              The digital memory of a football staff. Your program's mission, playbook, and AI
              coach — in one command center.
            </p>
          </div>
          <div className="text-xs font-condensed tracking-[0.3em] uppercase text-slate-500">
            Built for coaches. By the standards you set.
          </div>
        </div>
      </div>

      {/* Right — auth */}
      <div className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <div className="text-[10px] font-condensed tracking-[0.4em] uppercase text-amber mb-3">Sign in</div>
            <div className="font-display text-5xl tracking-wider text-white uppercase leading-none">Coach Access</div>
            <p className="text-slate-400 text-sm mt-3">Use your staff credentials. Only rostered coaches get in.</p>
          </div>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <Label className="text-[10px] font-condensed tracking-[0.3em] uppercase text-slate-400">Email</Label>
              <Input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 bg-surface border-surface-3 focus:border-amber focus-visible:ring-amber h-11 text-sm"
                required
                autoFocus
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-[10px] font-condensed tracking-[0.3em] uppercase text-slate-400">Password</Label>
                <Link to="/forgot-password" data-testid="link-forgot-password"
                  className="text-[10px] font-condensed tracking-[0.2em] uppercase text-amber hover:underline">
                  Forgot password?
                </Link>
              </div>
              <Input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 bg-surface border-surface-3 focus:border-amber focus-visible:ring-amber h-11 text-sm"
                required
              />
            </div>

            {error && (
              <div className="text-sm text-red-400 border-l-2 border-red-500 pl-3 py-2 bg-red-500/5" data-testid="login-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={busy}
              data-testid="login-submit"
              className="w-full h-12 bg-amber hover:bg-amber-600 text-[#0B0D12] font-condensed uppercase tracking-[0.25em] text-sm rounded-none"
            >
              {busy ? "Signing in…" : "Enter Gridiron OS"}
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-surface-3">
            <div className="text-xs text-slate-500 mb-3 font-condensed uppercase tracking-widest">Sample credentials</div>
            <div className="text-xs text-slate-400 font-mono">
              coach@titans.football / titans2026
            </div>
            <div className="mt-6 text-xs text-slate-500">
              New coach? <Link to="/register" className="text-amber hover:underline" data-testid="link-register">Request an account</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
